import React, { useState } from 'react';
import {
    Box, Typography, IconButton, Tooltip,
    Drawer, Fab, useMediaQuery, useTheme, Slide
} from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useCollectionChat } from './hooks/useCollectionChat';
import ChatMessages from './ChatMessages';
import SuggestedQuestions from './SuggestedQuestions';
import ChatInput from './ChatInput';
import EmptyState from './EmptyState';

const SIDEBAR_WIDTH = 320;
const BOTTOM_SHEET_HEIGHT = '75vh';

export const CollectionAISidebar: React.FC = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isOpen, setIsOpen] = useState(false);
    const { messages, isLoading, suggestions, sendMessage, clearConversation } = useCollectionChat();
    const [input, setInput] = useState('');

    const handleSend = () => {
        if (!input.trim()) return;
        sendMessage(input.trim());
        setInput('');
    };

    const isEmpty = messages.length === 0;

    // ── Shared sidebar content ─────────────────────────────────────────
    const sidebarContent = (
        <>
            <Box sx={{
                px: 1.5, py: 1.25,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{
                        width: 24, height: 24, borderRadius: 1,
                        bgcolor: '#EEEDFE', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <AutoFixHighOutlinedIcon sx={{ fontSize: 14, color: '#534AB7' }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        Collection intelligence
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="New conversation">
                        <IconButton size="small" onClick={clearConversation}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="History">
                        <IconButton size="small">
                            <HistoryOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    {!isDesktop && (
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={() => setIsOpen(false)}>
                                <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {isEmpty ? (
                <Box sx={{
                    flex: 1, overflowY: 'auto', minHeight: 0,
                    px: 1.5, py: 1.5,
                    display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                    <EmptyState onSuggest={sendMessage} />
                    <SuggestedQuestions
                        suggestions={suggestions}
                        onSelect={sendMessage}
                        variant="list"
                    />
                </Box>
            ) : (
                <ChatMessages messages={messages} onFollowUp={sendMessage} />
            )}

            {!isEmpty && (
                <SuggestedQuestions
                    suggestions={suggestions}
                    onSelect={sendMessage}
                    variant="chips"
                />
            )}

            <ChatInput
                value={input}
                onChange={setInput}
                onSend={handleSend}
                isLoading={isLoading}
            />
        </>
    );

    // ── Desktop — permanent sidebar ────────────────────────────────────
    if (isDesktop) {
        return (
            <Box sx={{
                width: 320,
                minWidth: 320,
                flexShrink: 0,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderLeft: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}>
                {sidebarContent}
            </Box>
        );
    }

    // ── Tablet — FAB + right drawer ────────────────────────────────────
    if (isTablet) {
        return (
            <>
                {!isOpen && (
                    <Fab
                        onClick={() => setIsOpen(true)}
                        size="medium"
                        sx={{
                            position: 'fixed',
                            bottom: 72,
                            right: 16,
                            bgcolor: '#534AB7',
                            color: '#fff',
                            zIndex: theme.zIndex.fab,
                            '&:hover': { bgcolor: '#3C3489' },
                        }}
                        aria-label="Open collection intelligence"
                    >
                        <AutoFixHighOutlinedIcon />
                    </Fab>
                )}
                <Drawer
                    anchor="right"
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            width: 320,
                            display: 'flex',
                            flexDirection: 'column',
                            bottom: 56,
                            height: 'calc(100% - 56px)',
                            top: 'auto',
                        },
                    }}
                >
                    {sidebarContent}
                </Drawer>
            </>
        );
    }

    // ── Mobile — collapsed bar + bottom sheet ──────────────────────────
    return (
        <>
            {/* Collapsed trigger bar */}
            <Box
                onClick={() => setIsOpen(true)}
                sx={{
                    display: isOpen ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 22, height: 22, borderRadius: 1,
                        bgcolor: '#EEEDFE', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <AutoFixHighOutlinedIcon sx={{ fontSize: 13, color: '#534AB7' }} />
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        Collection intelligence
                    </Typography>
                    {messages.length > 0 && (
                        <Box sx={{
                            fontSize: 11, px: 0.75, py: 0.25,
                            bgcolor: '#EEEDFE', color: '#534AB7',
                            borderRadius: '99px', fontWeight: 500,
                        }}>
                            {messages.length}
                        </Box>
                    )}
                </Box>
                <KeyboardArrowUpOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </Box>

            {/* Bottom sheet */}
            <Drawer
                anchor="bottom"
                open={isOpen}
                onClose={() => setIsOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        height: '75vh',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '12px 12px 0 0',
                        mb: '56px',
                    },
                }}
            >
                {/* Drag handle */}
                <Box sx={{
                    display: 'flex', justifyContent: 'center',
                    pt: 1, pb: 0.5, flexShrink: 0,
                }}>
                    <Box sx={{
                        width: 32, height: 4,
                        borderRadius: 2, bgcolor: 'divider',
                    }} />
                </Box>
                {sidebarContent}
            </Drawer>
        </>
    );
};